/**
 * Header the backend requires on every fund/grant/FX write. Omitting it (or sending it blank) is
 * answered with a hard 400 — there is no lenient mode.
 */
export const IDEMPOTENCY_KEY_HEADER = 'X-Idempotency-Key';

/**
 * The write endpoints that require `X-Idempotency-Key`.
 *
 * THIS LIST IS THE ONLY WIRING NEEDED. IdempotencyInterceptor attaches a correct key to every POST
 * whose path matches an entry here, so adding a new idempotent write endpoint means adding one line
 * below — not touching the API service, the calling component, or the form manager.
 */
export const IDEMPOTENT_WRITE_PATH = {
  CONTRIBUTE_TO_YOUR_FUND: '/transaction/contribute-to-your-fund',
  GRANT_TO_A_FUND: '/transaction/grant-to-a-fund',
  FUND_TO_FUND_TRANSFER: '/transaction/fund-to-fund-transfer',
  CREATE_FX: '/transaction/create-fx',

  // Payment writes. Confirmed with backend as the /payment routes that enforce the header — note the
  // recurring variants (/subscribe, /subscriptionSuccessHandler) and /purchase are deliberately NOT
  // here, so do not add them without checking the backend enforces and CORS-allows the header there.
  OFFLINE_TRANSFERS: '/payment/offlineTransfers',
  PURCHASE_SUCCESS_HANDLER: '/payment/successHandler',
  EXCHANGE_TOKEN: '/payment/exchangeToken',
} as const;

export type IdempotentWritePath = (typeof IDEMPOTENT_WRITE_PATH)[keyof typeof IDEMPOTENT_WRITE_PATH];

const IDEMPOTENT_WRITE_PATHS: IdempotentWritePath[] = Object.values(IDEMPOTENT_WRITE_PATH);

/**
 * The registered path this request writes to, or `undefined` if it needs no key.
 * Matches on path only, so query strings and the environment's API base url are irrelevant.
 */
export function findIdempotentWritePath(url: string): IdempotentWritePath | undefined {
  const path = url.split(/[?#]/)[0];
  return IDEMPOTENT_WRITE_PATHS.find((writePath) => path.endsWith(writePath));
}

/**
 * UUID v4 for the idempotency header.
 *
 * `crypto.randomUUID()` only exists in secure contexts, so fall back to `crypto.getRandomValues`
 * and finally to `Math.random`. A weaker key still lets the gateway dedupe; no key at all means a
 * 400 and a dead donate/grant/transfer button.
 */
export function generateIdempotencyKey(): string {
  const webCrypto = typeof crypto !== 'undefined' ? crypto : undefined;

  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof webCrypto?.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Version 4, variant 1 (RFC 4122).
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * 400 on an idempotent write means the request was malformed — for this contract, a missing or blank
 * header. That is a frontend defect, not something a user can fix by trying again, so surface it as
 * unexpected rather than as "retry later".
 */
export function isMissingIdempotencyKeyError(error: any): boolean {
  return error?.status === 400;
}

/**
 * 409 means the same key arrived with a *different* payload (a conflicting replay). It is a genuine
 * failure: retrying with the same key can only conflict again, so the key is released.
 * Not reachable on `create-fx`, which validates the header but has no gateway call behind it.
 */
export function isIdempotencyConflictError(error: any): boolean {
  return error?.status === 409;
}
